import { useState, useEffect } from 'react';
import api from '../services/api';
import {
  LuUsers, LuPlus, LuPencil, LuTrash2, LuSearch, LuX, LuCheck,
} from 'react-icons/lu';

const emptyForm = { full_name: '', position: '', registration: '', department_id: '', company_id: '', active: true };

function EmployeeModal({ editing, departments, companies, onClose, onSaved }) {
  const [form,   setForm]   = useState(
    editing
      ? {
          full_name:     editing.full_name     || '',
          position:      editing.position      || '',
          registration:  editing.registration  || '',
          department_id: editing.department_id != null ? String(editing.department_id) : '',
          company_id:    editing.company_id    != null ? String(editing.company_id)    : '',
          active:        editing.active !== false,
        }
      : emptyForm,
  );
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  // Mais de uma empresa no sistema = ambíguo, precisa perguntar explicitamente.
  // Com só 1, nem mostra o campo — o backend já resolve sozinho nesse caso.
  const showCompanyField = companies.length > 1;

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  // Ao trocar o departamento, pré-preenche a empresa a partir dele (cada
  // departamento pertence a uma única empresa) — mas deixa o campo visível
  // pra corrigir manualmente quando o departamento estiver órfão (company_id
  // nulo, dado de antes do sistema virar multiempresa) ou quando o valor
  // herdado estiver errado.
  const handleDepartmentChange = (deptId) => {
    const dept = departments.find((d) => String(d.id) === String(deptId));
    setForm((f) => ({
      ...f,
      department_id: deptId,
      company_id: dept?.company_id != null ? String(dept.company_id) : f.company_id,
    }));
  };

  const handleSave = async () => {
    if (!form.full_name.trim()) { setError('Nome é obrigatório'); return; }
    if (!form.department_id)    { setError('Departamento é obrigatório'); return; }
    if (showCompanyField && !form.company_id) { setError('Empresa é obrigatória'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, company_id: form.company_id || undefined };

      if (editing) {
        await api.put(`/employees/${editing.id}`, payload);
      } else {
        await api.post('/employees', payload);
      }
      // recarrega lista completa para ter departamento incluso
      const { data } = await api.get('/employees');
      onSaved(data, !!editing);
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.38)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface-1)', borderRadius: 12, width: '100%', maxWidth: 480,
        boxShadow: '0 12px 40px rgba(0,0,0,0.20)', maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-1)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <LuUsers color="var(--accent)" />
            {editing ? 'Editar Funcionário' : 'Novo Funcionário'}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: 4 }}>
            <LuX size={17} />
          </button>
        </div>

        <div style={{ padding: '16px 24px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {error && <div className="alert-custom danger" style={{ fontSize: 13 }}>{error}</div>}

          <div>
            <label className="form-label">Nome completo *</label>
            <input className="form-control" value={form.full_name} autoFocus
              onChange={(e) => set('full_name', e.target.value)} placeholder="Nome completo" />
          </div>

          <div>
            <label className="form-label">Departamento *</label>
            <select className="form-control" value={form.department_id}
              onChange={(e) => handleDepartmentChange(e.target.value)}>
              <option value="">Selecione…</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>

          {showCompanyField && (
            <div>
              <label className="form-label">Empresa *</label>
              <select className="form-control" value={form.company_id}
                onChange={(e) => set('company_id', e.target.value)}>
                <option value="">Selecione…</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.trade_name || c.name}</option>
                ))}
              </select>
              <small style={{ fontSize: 11, color: 'var(--text-3)' }}>
                Preenchida automaticamente ao escolher o departamento — ajuste aqui se o departamento não tiver empresa vinculada.
              </small>
            </div>
          )}

          <div className="resp-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Cargo</label>
              <input className="form-control" value={form.position}
                onChange={(e) => set('position', e.target.value)} placeholder="Ex: Operador" />
            </div>
            <div>
              <label className="form-label">Matrícula</label>
              <input className="form-control" value={form.registration}
                onChange={(e) => set('registration', e.target.value)} placeholder="Ex: 001" />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" id="chk-active" checked={form.active}
              onChange={(e) => set('active', e.target.checked)}
              style={{ width: 15, height: 15, cursor: 'pointer' }} />
            <label htmlFor="chk-active" style={{ fontSize: 13, color: 'var(--text-2)', cursor: 'pointer', marginBottom: 0 }}>
              Funcionário ativo
            </label>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button onClick={onClose} style={{
              padding: '9px 20px', border: '1px solid var(--border)', borderRadius: 8,
              background: 'var(--surface-1)', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', color: 'var(--text-2)',
            }}>
              Cancelar
            </button>
            <button onClick={handleSave} disabled={saving} style={{
              padding: '9px 22px', border: 'none', borderRadius: 8, cursor: 'pointer',
              fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
              background: 'var(--accent)', color: 'var(--on-accent)',
              display: 'flex', alignItems: 'center', gap: 6,
              opacity: saving ? 0.7 : 1,
            }}>
              <LuCheck /> {saving ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EmployeesPage() {
  const [employees,   setEmployees]   = useState([]);
  const [departments, setDepartments] = useState([]);
  const [companies,   setCompanies]   = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [search,      setSearch]      = useState('');
  const [deptFilter,  setDeptFilter]  = useState('');
  const [modal,       setModal]       = useState(null); // null | 'new' | <employee obj>
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get('/employees'),
      api.get('/departments'),
      // Sempre o conjunto COMPLETO de empresas, ignorando a "empresa ativa"
      // do CompanySwitcher — o campo de empresa deste formulário precisa
      // listar todas as opções possíveis, não só a que está ativa no momento.
      api.get('/companies', { headers: { 'X-Active-Company-Id': '' } }),
    ])
      .then(([e, d, c]) => {
        if (cancelled) return;
        setEmployees(e.data);
        setDepartments(d.data);
        setCompanies(c.data);
      })
      .catch(() => { if (!cancelled) setError('Erro ao carregar dados'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleSaved = (newList, isEdit) => {
    setEmployees(newList);
    setModal(null);
    setSuccess(isEdit ? 'Funcionário atualizado' : 'Funcionário cadastrado');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleDelete = async (emp) => {
    if (!window.confirm(`Remover "${emp.full_name}"?`)) return;
    try {
      await api.delete(`/employees/${emp.id}`);
      setEmployees((p) => p.filter((e) => e.id !== emp.id));
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao remover');
    }
  };

  const filtered = employees.filter((e) => {
    const matchDept = !deptFilter || String(e.department_id) === deptFilter;
    const q = search.toLowerCase();
    const matchSearch = !q
      || e.full_name.toLowerCase().includes(q)
      || (e.registration || '').toLowerCase().includes(q)
      || (e.position || '').toLowerCase().includes(q);
    return matchDept && matchSearch;
  });

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <div className="page-card-title">Funcionários</div>
          <div className="page-card-subtitle">Cadastre e gerencie os funcionários por departamento</div>
        </div>
        <button className="btn-salvar" onClick={() => setModal('new')}>
          <LuPlus style={{ marginRight: 6 }} /> Novo Funcionário
        </button>
      </div>

      {error   && <div className="alert-custom danger"  style={{ marginBottom: 12 }}>{error}</div>}
      {success && <div className="alert-custom success" style={{ marginBottom: 12 }}>{success}</div>}

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 180 }}>
          <LuSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }} />
          <input className="form-control" style={{ paddingLeft: 32 }}
            placeholder="Buscar por nome, cargo ou matrícula…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="form-control" style={{ flex: '0 0 200px' }}
          value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
          <option value="">Todos os departamentos</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>

      {loading && <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '16px 0' }}>Carregando…</div>}

      {!loading && filtered.length === 0 && (
        <div className="empty-state">
          <LuUsers size={32} color="var(--text-3)" style={{ marginBottom: 8 }} />
          <p>{employees.length === 0 ? 'Nenhum funcionário cadastrado' : 'Nenhum resultado para os filtros selecionados'}</p>
        </div>
      )}

      {filtered.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                {['Nome', 'Departamento', 'Cargo', 'Matrícula', 'Status', ''].map((h) => (
                  <th key={h} style={{ padding: '9px 12px', textAlign: 'left', color: 'var(--text-3)', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((emp) => (
                <tr key={emp.id} style={{ borderBottom: '1px solid var(--border)' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '11px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 34, height: 34, borderRadius: '50%', background: 'var(--surface-3)', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'var(--accent)', fontWeight: 800, fontSize: 14,
                      }}>
                        {emp.full_name.charAt(0).toUpperCase()}
                      </div>
                      <span style={{ fontWeight: 700, color: 'var(--text-1)' }}>{emp.full_name}</span>
                    </div>
                  </td>
                  <td style={{ padding: '11px 12px', color: 'var(--text-2)' }}>
                    {emp.department?.name || '—'}
                  </td>
                  <td style={{ padding: '11px 12px', color: 'var(--text-2)' }}>{emp.position || '—'}</td>
                  <td style={{ padding: '11px 12px', color: 'var(--text-2)' }}>{emp.registration || '—'}</td>
                  <td style={{ padding: '11px 12px' }}>
                    <span style={{
                      padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                      background: emp.active ? 'var(--success-soft)' : 'var(--surface-2)',
                      color: emp.active ? 'var(--success)' : 'var(--text-3)',
                    }}>
                      {emp.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td style={{ padding: '11px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button onClick={() => setModal(emp)} title="Editar" style={iconBtn('var(--text-1)')}>
                      <LuPencil size={13} />
                    </button>
                    <button onClick={() => handleDelete(emp)} title="Remover" style={iconBtn('var(--danger)')}>
                      <LuTrash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '10px 4px' }}>
            {filtered.length} funcionário{filtered.length !== 1 ? 's' : ''} encontrado{filtered.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {modal && (
        <EmployeeModal
          editing={modal === 'new' ? null : modal}
          departments={departments}
          companies={companies}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

const iconBtn = (color) => ({
  background: 'none', border: 'none', cursor: 'pointer', color,
  padding: '4px 6px', borderRadius: 5,
});
